import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDemoStore, type POI, type Alert } from '../store/demoStore';
import { api, type AlertFromAPI, type StoreData } from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useTranslation } from 'react-i18next';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import Header from '../components/layout/Header';
import { Store, Users, AlertCircle, Activity, Shield, Settings, Plus, X, Car, MapPin } from 'lucide-react';
import StoreSelectorModal from '../components/StoreSelectorModal';
import { useNavigate } from 'react-router-dom'; 
import { PoiListManager } from '../components/PoiListManager';
import { AuthorizedPersonsManager, AuthorizedPlatesManager } from '../components/AuthorizedManagers';
import StoreMultiSelectModal from '../components/StoreMultiSelectModal';
import BulkPOIUpload from '../components/BulkPOIUpload';
import TrainingSimulation from '../components/TrainingSimulation';
import { POIDetailModal } from '../components/POIDetailModal';
import UserAddToStoresModal from '../components/UserAddToStoresModal';
import { useModal } from '../context/ModalContext';
import ClientFooter from '../components/layout/ClientFooter';
import { useConnectivity } from '../hooks/useConnectivity';
import OfflineShield from '../components/OfflineShield';
// v0.4.6i: replaces the single 🔑 reset-password button with a
// 2-option dialog (Reset password / Reset device).
import { AdminResetDialog } from '../components/AdminResetDialog';

// ---------- Alerts Log Modal ----------
function AlertsLogModal({ alerts, onClose, title, onPoiClick }: { alerts: AlertFromAPI[]; onClose: () => void; title?: string; onPoiClick?: (alert: AlertFromAPI) => void }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background rounded-lg shadow-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{title || t('admin.alerts24h')}</h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-muted"><X className="w-5 h-5" /></button>
        </div>
        {alerts.length === 0 ? (
          <p className="text-muted-foreground">{t('admin.noAlerts')}</p>
        ) : (
          <div className="space-y-2">
            {alerts.map(alert => (
              <Card
                key={alert.id}
                className={`p-3 text-sm ${(alert.poi || alert.event_type === 'access_control') && onPoiClick ? 'cursor-pointer hover:shadow-md' : ''}`}
                onClick={() => {
                  if ((alert.poi || alert.event_type === 'access_control') && onPoiClick) {
                    onPoiClick(alert);
                  }
                }}
              >
                <div className="flex justify-between">
                  <span className="font-medium">
                    {alert.poi ? alert.poi.code : 'Access Control'}
                  </span>
                  <span className={
                    alert.status === 'active' ? 'text-red-600' :
                    alert.status === 'acknowledged' ? 'text-blue-600' : 'text-green-600'
                  }>
                    {alert.status}
                  </span>
                </div>
                  <p className="text-xs text-muted-foreground">
                    {new Date(alert.created_at).toLocaleString()} · {alert.event_type === 'access_control' ? 'Access Control' : 'Guardian'}
                  </p>
                  {alert.store_name && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" /> {alert.store_name}
                    </p>
                  )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function POIsFilterModal({ pois, title, onClose, showNetworkBadge }: { pois: POI[]; title: string; onClose: () => void; showNetworkBadge?: boolean }) {
  const [search, setSearch] = useState('');
  const filtered = pois.filter(p => p.code.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div className="bg-background rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{title} ({filtered.length})</h2>
          <button onClick={onClose} className="p-1"><X className="w-5 h-5" /></button>
        </div>
        <Input
          placeholder="Search code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-4"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(poi => (
            <Card key={poi.id} className="p-3 flex gap-3">
              <img src={poi.photoUrl} className="w-12 h-12 rounded-full object-cover" onError={(e) => (e.currentTarget.style.display='none')} />
              <div>
                <span className="font-mono text-sm">{poi.code}</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  poi.threatLevel === 'High' ? 'bg-red-100 text-red-800' :
                  poi.threatLevel === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-blue-100 text-blue-800'
                }`}>{poi.threatLevel}</span>
                {showNetworkBadge && (
                  <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                    Network
                    {(poi as any).storeName ? ` (${(poi as any).storeName})` : ''}
                  </span>
                )}
              </div>
            </Card>
          ))}
          {filtered.length === 0 && <p className="text-sm text-muted-foreground">No matches.</p>}
        </div>
      </div>
    </div>
  );
}

interface ClientAlert {
  id: string;
  status: string;
  acknowledged_by: string | null;
  acknowledged_by_name?: string | null;
  store_name: string;
  event_type: string;
  created_at: string;
}

interface StoreUser {
  id: string;
  email: string;
  name: string;
  role: string;
  role_in_store: string;
  training_enabled?: boolean; 
  is_primary_sadmin?: boolean;
}

export default function ClientDashboard() {
  const { user, activeStoreId } = useAuth();
  const { isOnline } = useConnectivity();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [stores, setStores] = useState<StoreData[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<ClientAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [pois, setPois] = useState<POI[]>([]);
  const [authorizedPersonsCount, setAuthorizedPersonsCount] = useState(0);
  const [authorizedPlatesCount, setAuthorizedPlatesCount] = useState(0);
  const { triggerAlert, mockPOIs, activeAlerts, clearAllAlerts, isDemoMode } = useDemoStore();
  const currentStoreId = activeStoreId || user?.storeId;

  // Store management modal state
  const [selectedStore, setSelectedStore] = useState<StoreData | null>(null);
  const [storeUsers, setStoreUsers] = useState<StoreUser[]>([]);
  const [storePoiCodes, setStorePoiCodes] = useState<string[]>([]);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userRole, setUserRole] = useState<'guard' | 'admin'>('guard');
  const [modalError, setModalError] = useState('');
  const [success, setSuccess] = useState('');
  // v0.4.6i: state for the reset-access dialog.
  const [resetTarget, setResetTarget] = useState<{ userId: string; userName: string; userEmail: string } | null>(null);

  const [addingUser, setAddingUser] = useState(false);  // v0.4.6: prevents double-submit
  const [showUserAddModal, setShowUserAddModal] = useState(false);
  const [userAddRole, setUserAddRole] = useState<'guard' | 'admin'>('guard');
  const [showOwnPoisModal, setShowOwnPoisModal] = useState(false);
  const [showNetworkPoisModal, setShowNetworkPoisModal] = useState(false);
  const [showPoiForm, setShowPoiForm] = useState(false);
  const [editingPoi, setEditingPoi] = useState<POI | null>(null);
  const [showAuthorizedModal, setShowAuthorizedModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showAuthorizedPersonsModal, setShowAuthorizedPersonsModal] = useState(false);
  const [showAuthorizedPlatesModal, setShowAuthorizedPlatesModal] = useState(false);
  const [pastAlertsCount, setPastAlertsCount] = useState(0);
  const [completedAlerts, setCompletedAlerts] = useState<AlertFromAPI[]>([]);
  const [alertsLogTitle, setAlertsLogTitle] = useState('');
  const { showAlert } = useModal();
  const { showConfirm } = useModal();

  // Edit state
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState<'guard' | 'admin'>('guard');
  const [editGlobalRole, setEditGlobalRole] = useState<'guard' | 'admin' | 'cadmin' | 'sadmin'>('guard');  const [userTrainingEnabled, setUserTrainingEnabled] = useState(false);
  const [editTrainingEnabled, setEditTrainingEnabled] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [userSearch, setUserSearch] = useState('');


  // Alerts Log Modal
  const [showAlertsLog, setShowAlertsLog] = useState(false);
  const [alertsLog, setAlertsLog] = useState<AlertFromAPI[]>([]);
  const [alertCount, setAlertCount] = useState(0);
  const [authorizedCount, setAuthorizedCount] = useState(0);
  const [selectedAlertPoi, setSelectedAlertPoi] = useState<any>(null);
  const [selectedAccessAlert, setSelectedAccessAlert] = useState<AlertFromAPI | null>(null);

  // Store selector for admin
  const [showStoreSelector, setShowStoreSelector] = useState(false);
  const [adminStores, setAdminStores] = useState<StoreData[]>([]);

  // State for aggregated lists
  const [allPersons, setAllPersons] = useState<any[]>([]);
  const [allPlates, setAllPlates] = useState<any[]>([]);
  const [markedPois, setMarkedPois] = useState<POI[]>([]);

  // Force live mode for any real account (non‑demo emails)
  useEffect(() => {
    if (user && user.email !== 'admin@isentinel360.com' && user.email !== 'guard@isentinel360.com') {
      useDemoStore.setState({ isDemoMode: false });
    }
  }, [user]);

  // ——— Core data: stores, alerts, POIs ———
  useEffect(() => {
    if (!user) return;

    // v0.4.9.1.9: distinguish between superadmin (no assigned client) and sadmin (has assigned client).
    // Previously the condition grouped them: any superadmin OR sadmin without selectedClientId got
    // an empty state. For superadmin this is correct (they must pick a client). But SAdmin always
    // has an assigned client_id (from their JWT) — they should NEVER see an empty state.
    // 
    // SAdmin's effective client is either their selectedClientId (manual override) OR their
    // assigned user.clientId. We resolve this once at the top of the effect.
    const effectiveClientId = (() => {
      if (user.role === 'superadmin') return selectedClientId;  // may be null
      if (user.role === 'sadmin') return selectedClientId || (user as any).clientId || null;
      return null;  // cadmin, admin, guard: their data is scoped by JWT
    })();

    // Only superadmin (no assigned client) shows empty state when no client selected
    if (user.role === 'superadmin' && !effectiveClientId) {
      setLoading(false);
      setPois([]);
      setStores([]);
      setAlerts([]);
      setAlertCount(0);
      setAuthorizedPersonsCount(0);
      setAuthorizedPlatesCount(0);
      return;
    }

  let cancelled = false;

  const fetchData = async () => {
    try {
      // Determine which client to fetch for
      const clientIdToUse =
        (user?.role === 'superadmin' || user?.role === 'sadmin') ? effectiveClientId : undefined;

      // Always fetch stores and alerts for the given client (or own client)
      const [storesData, alertsData] = await Promise.all([
        api.getStores(clientIdToUse),
        api.getClientAlerts(clientIdToUse),
      ]);
      if (cancelled) return;
      setStores(storesData);
      setAlerts(alertsData);
      setAlertCount(alertsData.length);

        // Fetch POIs for the selected client’s first store (includes network POIs)
        let poisData: any[] = [];
        if (storesData.length > 0) {
          try {
            poisData = await api.getPOIs(storesData[0].id);
          } catch (e) {
            console.error('Failed to fetch POIs', e);
          }
        }
        setPois(poisData);

        // Mark network POIs immediately using the stores we just fetched
        const clientStoreIds = new Set(storesData.map(s => s.id));
        const poisWithNetwork = poisData.map(p => ({
          ...p,
          isNetwork: !clientStoreIds.has(p.storeId!),
        }));
        setMarkedPois(poisWithNetwork);

        // Fetch authorized counts for all stores (same as before)
        let personsTotal = 0;
        let platesTotal = 0;
        for (const store of storesData) {
          const [persons, plates] = await Promise.all([
            api.getAuthorizedPersons(store.id).catch(() => []),
            api.getAuthorizedPlates(store.id).catch(() => []),
          ]);
          personsTotal += persons.length;
          platesTotal += plates.length;
        }
        if (!cancelled) {
          setAuthorizedPersonsCount(personsTotal);
          setAuthorizedPlatesCount(platesTotal);
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user, selectedClientId]);

  // --- Fetch the clients list for superadmin
  useEffect(() => {
    if (user?.role === 'superadmin' || user?.role === 'sadmin') {
      api.getClients().then(setClients).catch(console.error);
    }
  }, [user]);

  // --- Reset counts & POIs when the selected client changes
  useEffect(() => {
    setPois([]);
    setPastAlertsCount(0);
    setAuthorizedPersonsCount(0);
    setAuthorizedPlatesCount(0);
  }, [selectedClientId]);

  // ——— Completed alerts (last 24h) ———
  useEffect(() => {
    if (stores.length === 0) return;
    let cancelled = false;
    const fetchCompleted = async () => {
      let total = 0;
      const all: AlertFromAPI[] = [];
      for (const store of stores) {
        if (cancelled) break;
        try {
          const data = await api.getCompletedAlerts(store.id);
          total += data.length;
          all.push(...data);
        } catch (err) {
          console.error(err);
        }
      }
      if (!cancelled) {
        setPastAlertsCount(total);
        setCompletedAlerts(all);
      }
    };
    fetchCompleted();
    const interval = setInterval(fetchCompleted, 60000); // every minute
    return () => { cancelled = true; clearInterval(interval); };
  }, [stores, selectedClientId]);

  // Reset past alerts when client selection changes
  useEffect(() => {
    setPastAlertsCount(0);
    setCompletedAlerts([]);
  }, [selectedClientId]);

  // Fetch all stores when the modal open for Authorized persons
  useEffect(() => {
    if (showAuthorizedPersonsModal && stores.length > 0) {
      const fetchAll = async () => {
        const all: any[] = [];
        for (const store of stores) {
          try {
            const persons = await api.getAuthorizedPersons(store.id);
            all.push(...persons);
          } catch (e) {}
        }
        setAllPersons(all);
      };
      fetchAll();
    }
  }, [showAuthorizedPersonsModal, stores]);

  // Fetch all stores when the modal open for Authorized Licenses
  useEffect(() => {
    if (showAuthorizedPlatesModal && stores.length > 0) {
      const fetchAll = async () => {
        const all: any[] = [];
        for (const store of stores) {
          try {
            const plates = await api.getAuthorizedPlates(store.id);
            all.push(...plates);
          } catch (e) {}
        }
        setAllPlates(all);
      };
      fetchAll();
    }
  }, [showAuthorizedPlatesModal, stores]);

  // Store users fetch
  const fetchStoreUsers = async (storeId: string) => {
    try {
      const users = await api.getStoreUsers(storeId);
      setStoreUsers(users);
    } catch (err) {
      console.error(err);
    }
  };

  // Fetch alerts for the logs modal
  const fetchAlertsForLog = async () => {
    try {
      if (isDemoMode) {
        setAlertsLog(activeAlerts.map(a => ({
          id: a.id,
          poi: a.poi ? { code: a.poi.code, threatLevel: a.poi.threatLevel } : null,
          createdAt: a.timestamp.toISOString(),
          status: (a as any).status || 'active',
        })) as any);
        setShowAlertsLog(true);
      } else {
        // v0.4.9.1.9: include sadmin (was superadmin only)
        const clientIdToUse =
          (user?.role === 'superadmin' || user?.role === 'sadmin') 
            ? (selectedClientId || (user as any).clientId || undefined) 
            : undefined;
        const data = await api.getClientAlerts(clientIdToUse);
        setAlertsLog(data.map((a: any) => ({
          ...a,
          createdAt: a.created_at,
          poi: a.poi ?? null,
        })));
        setShowAlertsLog(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const refreshPois = async () => {
    if (!isDemoMode) {
      try {
        const data = await api.getPOIs();
        setPois(data);
      } catch (err) { console.error(err); }
    }
  };


  const handleEdit = (poi: POI) => {
    setEditingPoi(poi);
    setShowPoiForm(true);
  };

  const handleDeletePoi = async (poiId: string) => {
    if (!confirm(t('admin.confirmDeletePOI') || 'Are you sure?')) return;
    if (isDemoMode) {
      setPois(prev => prev.filter(p => p.id !== poiId));
    } else {
      try {
        await api.deletePOI(poiId);
        await refreshPois();
      } catch (err) {
        alert('Failed to delete POI');
      }
    }
  };

  // Remove user
  const handleRemoveUser = async (storeId: string, userId: string) => {
    const ok = await showConfirm('Remove this user from the store?');
    if (!ok) return;
    try {
      await api.removeStoreUser(storeId, userId);
      fetchStoreUsers(storeId);
      setSuccess('User removed');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err: any) { setModalError(err.message); }
  };

  // Add user
  const handleAddUser = async (storeId: string) => {
    // v0.4.9.2.4c5: validate BOTH fields and SAY WHY. The form marked
    // Name as required (`*`) but this guard returned silently when EMAIL
    // was empty — so typing a name and clicking Add did nothing at all,
    // no error, and the user never existed to be found by the search box.
    if (!userName.trim() || !userEmail.trim()) {
      setModalError('Name and email are both required.');
      return;
    }
    if (addingUser) return;  // v0.4.6: ignore re-entry while in flight
    setAddingUser(true);
    setModalError('');
    try {
      await api.addStoreUser(storeId, userEmail, userRole, userName, userRole === 'admin' ? userTrainingEnabled : false);
      setUserEmail(''); setUserName('');
      fetchStoreUsers(storeId);
      setSuccess('User added');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err: any) {
      setModalError(err.message || 'Failed to add user');
    } finally {
      setAddingUser(false);
    }
  };

  // Summary counts
  const pendingAlerts = alerts.filter(a => !a.acknowledged_by).length;
  const inProgressAlerts = alerts.filter(a => a.acknowledged_by && a.status !== 'completed').length;
  const displayPois = isDemoMode ? mockPOIs : markedPois;



  if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;

  // Training Feature if enabled
  const trainingEnabled =
  (user?.role === 'superadmin' || user?.role === 'sadmin')
    ? selectedClientId
      ? true // superadmin can always train on any client
      : false
    : (user?.trainingModeEnabled ?? false);

  return (
    <div className="h-dvh flex flex-col bg-background">
      <div className="flex-shrink-0">
        <Header view="admin" />
      </div>
      {!isOnline ? <OfflineShield /> : (<>

      
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto p-6">
        {(user?.role === 'superadmin' || user?.role === 'sadmin') && (
          <div className="mb-6">
            <label className="text-sm font-medium mr-2">{t('admin.selectClient')}:</label>
            <select
              value={selectedClientId || ''}
              onChange={e => setSelectedClientId(e.target.value || null)}
              className="p-2 border rounded-md bg-background"
            >
              <option value="">-- {t('admin.chooseClient')} --</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {!selectedClientId && (
              <p className="text-muted-foreground text-sm mt-2">{t('admin.selectClientPrompt')}</p>
            )}
          </div>
        )}
        <h1 className="text-2xl font-bold mb-6">Client Dashboard</h1>

          {/* Summary Cards */}
          {/* ────── Stats Cards ────── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {/* System Status */}
            <Card className="p-4">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium">{t('admin.systemStatus')}</span>
              </div>
              <p className="text-sm font-medium mt-2 text-green-600">{t('admin.operational')}</p>
            </Card>

            {/* Active Stores */}
            <Card className="p-4 cursor-pointer hover:shadow-md">
              <div className="flex items-center gap-2">
                <Store className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium">{t('admin.activeStores')}</span>
              </div>
              <p className="text-2xl font-bold mt-2">{stores.length}</p>
            </Card>

            {/* Total POIs – Own (clickable filter) */}
            <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => setShowOwnPoisModal(true)}>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm font-medium">{t('admin.totalPOIsOwn')}</span>
              </div>
              <p className="text-2xl font-bold mt-2">
                {markedPois.filter(p => !p.isNetwork).length}
              </p>
            </Card>

            {/* Total POIs – Network (clickable filter) */}
            <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => setShowNetworkPoisModal(true)}>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-500" />
                <span className="text-sm font-medium">{t('admin.totalPOIsNetwork')}</span>
              </div>
              <p className="text-2xl font-bold mt-2">
                {markedPois.filter(p => p.isNetwork).length}
              </p>
            </Card>

            {/* Alerts On‑Going (active alerts) */}
            <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => { 
              setAlertsLogTitle(t('admin.alertsOngoing')); 
              fetchAlertsForLog(); 
            }}>
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <span className="text-sm font-medium">{t('admin.alertsOngoing')}</span>
              </div>
              <p className="text-2xl font-bold mt-2">{alertCount || (isDemoMode ? 0 : '...')}</p>
            </Card>

            {/* Past Alerts (24h) – placeholder, will open same log for now */}
            <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => {
              setAlertsLog(completedAlerts);
              setShowAlertsLog(true);
            }}>
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-gray-500" />
                <span className="text-sm font-medium">{t('admin.pastAlerts24h')}</span>
              </div>
              <p className="text-2xl font-bold mt-2">{pastAlertsCount}</p>
            </Card>

            {/* Authorized Persons – clickable */}
            <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => setShowAuthorizedPersonsModal(true)}>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium">{t('admin.authorizedPersons')}</span>
              </div>
              <p className="text-2xl font-bold mt-2">{authorizedPersonsCount}</p>
            </Card>

            {/* Authorized Vehicles (License Plates) – clickable */}
            <Card className="p-4 cursor-pointer hover:shadow-md" onClick={() => setShowAuthorizedPlatesModal(true)}>
              <div className="flex items-center gap-2">
                <Car className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium">{t('admin.authorizedPlates')}</span>
              </div>
              <p className="text-2xl font-bold mt-2">{authorizedPlatesCount}</p>
            </Card>

          </div>

          {/* Training Mode if enabled */} 
          <div>
          {trainingEnabled && (
            <TrainingSimulation
              availableStores={stores.map(s => ({ id: s.id, name: s.name }))}
              showAlways={   // visible for superadmin only when a client is selected, and for cadmin based on their premium flag
                user?.role === 'superadmin'
                  ? !!selectedClientId
                  : !!user?.trainingModeEnabled
              }
              onAlertCreated={() => {
                // Refresh after a short delay to allow DB commit
                setTimeout(async () => {
                  try {
                    const alertsData = await api.getClientAlerts();
                    setAlerts(alertsData);
                    setAlertCount(alertsData.length);
                  } catch (err) {
                    console.error(err);
                  }
                }, 500);
              }}
            />
          )}
          </div>

          {/* Stores Management */}
          <h2 className="text-xl font-semibold">Stores</h2>
          <div className="flex justify-between items-center mb-4">
          <div>
          <Button variant="outline" size="sm" onClick={() => { setUserAddRole('guard'); setShowUserAddModal(true); }}>
            <Plus className="w-4 h-4 mr-1" /> {t('admin.addGuardToStores')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setUserAddRole('admin'); setShowUserAddModal(true); }}>
            <Plus className="w-4 h-4 mr-1" /> {t('admin.addAdminToStores')}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowRemoveModal(true)}>
            <X className="w-4 h-4 mr-1" /> {t('admin.removeFromStores')}
          </Button>
          </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stores.map(store => (
              <Card key={store.id} className="p-4 hover:shadow-md">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold">{store.name}</h3>
                    <p className="text-xs font-mono text-muted-foreground">{store.code}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => {
                    setSelectedStore(store);
                    fetchStoreUsers(store.id);
                    api.getStorePois(store.id).then(data => {
                      setStorePoiCodes(data.map((p: any) => p.code));
                    }).catch(err => {
                      console.error(err);
                      setStorePoiCodes([]);
                    });
                  }}>
                    <Settings className="w-3 h-3 mr-1" /> Manage
                  </Button>
                </div>
                <div className="mt-2 text-sm">
                  <p>Guardian: {store.guardian_enabled ? 'On' : 'Off'} · Access Control: {store.access_control_enabled ? 'On' : 'Off'}</p>
                  <p>Max Guards: {store.max_guards}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>

            
</>)}
      <ClientFooter />

      {/* ALL MODALS */}

      {/* Store Management Modal */}
      <Dialog open={!!selectedStore} onOpenChange={(open) => { if (!open) setSelectedStore(null); }}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:w-[calc(100vw-4rem)] md:max-w-2xl lg:max-w-5xl max-h-[90vh] sm:max-h-[85vh] overflow-y-auto">
          {selectedStore && (
            <>
              <DialogHeader>
                <DialogTitle className="pr-2" style={{ maxWidth: '85vw', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {selectedStore?.name}</DialogTitle>
              </DialogHeader>

              {modalError && <div className="p-2 mb-2 bg-red-100 text-red-800 rounded text-sm">{modalError}</div>}
              {success && <div className="p-2 mb-2 bg-green-100 text-green-800 rounded text-sm">{success}</div>}

              {/* User Management */}
              <div className="space-y-4 border-b pb-4">
                <h3 className="font-semibold text-lg">Users</h3>
                <div className="space-y-3">

                    {/* Sacrificial hidden inputs to absorb autofill */}
                    <input type="text" name="fake-usern" style={{ display: 'none' }} aria-hidden="true" />
                    <input type="password" name="fake-passw" style={{ display: 'none' }} aria-hidden="true" />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                    <Input placeholder="Name" value={userName} onChange={(e) => setUserName(e.target.value)} className="w-full" />
                  </div>
                  <div>
                    {/* v0.4.9.2.4c5: Email is required by both the client
                        guard and POST /api/stores/:id/users. It was missing the
                        red `*`, so the form lied about what was mandatory. */}
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                    <Input placeholder="User email" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} className="w-full" autoComplete="off" />
                  </div>
                  {userRole === 'admin' && (
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="checkbox"
                        id="training_enabled"
                        checked={userTrainingEnabled}
                        onChange={(e) => setUserTrainingEnabled(e.target.checked)}
                      />
                      <label htmlFor="training_enabled" className="text-sm">Training Module</label>
                    </div>
                  )}
                  <select value={userRole} onChange={(e) => setUserRole(e.target.value as any)} className="w-full p-2 border border-gray-300 rounded">
                    <option value="guard">Guard</option>
                    <option value="admin">Admin (superadmin only)</option>
                  </select>
                  <Button onClick={() => handleAddUser(selectedStore.id)} disabled={addingUser}>
                    {addingUser ? (t('common.loading') || 'Loading…') : 'Add'}
                  </Button>
                </div>
                {/* Store Role */}
                {/* User list with edit/delete */}
                <Input
                  placeholder={t('admin.searchUsers')}
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="mb-2"
                />
                <div className="border rounded divide-y">
                  {storeUsers.length === 0 && <p className="p-3 text-muted-foreground text-sm">No users yet</p>}
                  {storeUsers
                    .filter(u => {
                      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                      // 🔒 Cadmin: explicitly reject SADMIN and SUPERADMIN
                      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                      if (user?.role === 'cadmin') {
                        if (u.role === 'sadmin' || u.role === 'superadmin') {
                          // 🔒 Explicitly return false to hide these users
                          //console.log('[FILTER] Hiding user:', u.email, 'role:', u.role);
                          return false;
                        }
                      }
                      
                      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                      // 🔍 Search filter for allowed users
                      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                      const searchLower = userSearch.toLowerCase().trim();
                      if (!searchLower) return true;
                      
                      return (u.name || '').toLowerCase().includes(searchLower) ||
                            (u.email || '').toLowerCase().includes(searchLower);
                    })
                    .map(u => {
                      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                      // 🔒 Permission checks
                      // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                      const isSuperadmin = user?.role === 'superadmin';
                      const isSadmin = user?.role === 'sadmin';
                      const isCadmin = user?.role === 'cadmin';
                      const isTargetSadmin = u.role === 'sadmin';
                      const isTargetSuperadmin = u.role === 'superadmin';
                      const isTargetPrimary = u.is_primary_sadmin || false;
                      const isSelf = u.id === user?.id;

                      // 🔒 Edit/Delete permissions
                      const canEdit = isSuperadmin || 
                        (isSadmin && !isTargetSadmin && !isTargetSuperadmin && !isTargetPrimary && !isSelf) ||
                        (isCadmin && !isTargetSadmin && !isTargetSuperadmin && !isTargetPrimary && !isSelf && u.role !== 'cadmin');

                      const canDelete = isSuperadmin || 
                        (isSadmin && !isTargetSadmin && !isTargetSuperadmin && !isTargetPrimary && !isSelf) ||
                        (isCadmin && !isTargetSadmin && !isTargetSuperadmin && !isTargetPrimary && !isSelf && u.role !== 'cadmin');

                      // 🔒 Reset Password visibility:
                      // - SUPERADMIN: sees for all users
                      // - SADMIN: sees for Guards, Admins, Cadmins (NOT SADMIN, NOT SUPERADMIN, NOT self)
                      // - CADMIN: sees for Guards and Admins only (NOT Cadmins, NOT SADMIN, NOT SUPERADMIN, NOT self)
                      let showResetPassword = false;
                      if (isSuperadmin) {
                        showResetPassword = true;
                      } else if (isSadmin) {
                        showResetPassword = !isTargetSadmin && !isTargetSuperadmin && !isTargetPrimary && !isSelf;
                      } else if (isCadmin) {
                        showResetPassword = u.role === 'guard' || u.role === 'admin';
                      }

                      return (
                        <div key={`user-${u.id}-${editingUserId}`} className="flex items-center justify-between p-3">
                          {editingUserId === u.id ? (
                            <div className="flex-1 space-y-2">
                              <label className="text-xs text-muted-foreground">Name</label>
                              <Input placeholder="Name" value={editName} onChange={(e) => setEditName(e.target.value)} />
                              
                              <label className="text-xs text-muted-foreground">Email</label>
                              <Input placeholder="Email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
                              
                              <label className="text-xs text-muted-foreground">Store Role</label>
                              <select 
                                value={editRole} 
                                onChange={(e) => setEditRole(e.target.value as 'guard' | 'admin')} 
                                className="w-full p-2 border rounded"
                              >
                                <option value="guard">Guard</option>
                                <option value="admin">Admin</option>
                              </select>
                              
                              {editRole === 'admin' && (
                                <div className="flex items-center gap-2 mt-2">
                                  <input
                                    type="checkbox"
                                    checked={editTrainingEnabled}
                                    onChange={(e) => setEditTrainingEnabled(e.target.checked)}
                                  />
                                  <label className="text-sm">Training Module</label>
                                </div>
                              )}
                              
                              {(isSuperadmin || isSadmin) && (
                                <div>
                                  <label className="text-xs text-muted-foreground">Global Role</label>
                                  <select 
                                    value={editGlobalRole} 
                                    onChange={(e) => {
                                      const newGlobalRole = e.target.value as 'guard' | 'admin' | 'cadmin' | 'sadmin';
                                      setEditGlobalRole(newGlobalRole);
                                      if (newGlobalRole === 'sadmin' || newGlobalRole === 'cadmin') {
                                        setEditRole('admin');
                                      } else if (newGlobalRole === 'guard') {
                                        setEditRole('guard');
                                      } else if (newGlobalRole === 'admin') {
                                        setEditRole('admin');
                                      }
                                    }}
                                    className="w-full p-2 border rounded mt-1"
                                  >
                                    <option value="guard">Guard</option>
                                    <option value="admin">Admin</option>
                                    <option value="cadmin">Chief Admin</option>
                                    {isSuperadmin && (
                                      <option value="sadmin">SAdmin</option>
                                    )}
                                  </select>
                                  <p className="text-[10px] text-muted-foreground mt-1">
                                    {isSuperadmin
                                      ? 'You can assign SAdmin role.'
                                      : 'Only SUPERADMIN can assign SAdmin role.'}
                                  </p>
                                </div>
                              )}
                              
                              <div className="flex gap-2">
                                <Button size="sm" onClick={async () => {
                                  try {
                                    if ((u.role === 'superadmin' || u.role === 'sadmin') && !isSuperadmin) {
                                      alert('You cannot modify Super Admin or SAdmin users.');
                                      return;
                                    }

                                    let storeRole: 'guard' | 'admin' = editRole;
                                    if (editGlobalRole === 'sadmin' || editGlobalRole === 'cadmin') {
                                      storeRole = 'admin';
                                    }

                                    const globalRole = isSuperadmin ? editGlobalRole : undefined;

                                    await api.updateStoreUser(selectedStore!.id, u.id, {
                                      name: editName, 
                                      email: editEmail, 
                                      role_in_store: storeRole,
                                      role: globalRole,
                                      training_enabled: editRole === 'admin' ? editTrainingEnabled : false,
                                    });
                                    setEditingUserId(null);
                                    fetchStoreUsers(selectedStore!.id);
                                  } catch (err: any) { 
                                    setModalError(err.message); 
                                  }
                                }}>Save</Button>
                                <Button size="sm" variant="outline" onClick={() => setEditingUserId(null)}>Cancel</Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div>
                                <p className="font-medium">
                                  {u.name}
                                  {u.is_primary_sadmin && (
                                    <span className="ml-2 text-xs text-amber-600 font-semibold">🔒 Primary</span>
                                  )}
                                  {u.role === 'superadmin' && (
                                    <span className="ml-2 text-xs text-red-600 font-semibold">🔒 Super Admin</span>
                                  )}
                                  {u.role === 'sadmin' && (
                                    <span className="ml-2 text-xs text-purple-600 font-semibold">🔒 SAdmin</span>
                                  )}
                                  {u.role === 'cadmin' && (
                                    <span className="ml-2 text-xs text-blue-600 font-semibold">🔒 Chief Admin</span>
                                  )}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {u.email} · {u.role_in_store}
                                  {u.role !== u.role_in_store && (
                                    <span className="ml-1 font-medium text-blue-600 dark:text-blue-400">
                                      (Global: {u.role})
                                    </span>
                                  )}
                                </p>
                              </div>
                              
                              <div className="flex gap-2">
                                {u.id !== user?.id && (
                                  <>
                                    {canEdit && (
                                      <Button size="sm" variant="outline" onClick={() => {
                                        const currentUser = storeUsers.find(user => user.id === u.id);
                                        if (!currentUser) return;
                                        
                                        setEditingUserId(currentUser.id);
                                        setEditName(currentUser.name || '');
                                        setEditEmail(currentUser.email || '');
                                        setEditRole(currentUser.role_in_store as 'guard' | 'admin');
                                        setEditGlobalRole(currentUser.role as 'guard' | 'admin' | 'cadmin' | 'sadmin');
                                        setEditTrainingEnabled(currentUser.training_enabled || false);
                                      }}>Edit</Button>
                                    )}
                                    
                                    {canDelete && (
                                      <Button size="sm" variant="destructive" onClick={() => {
                                        if (u.is_primary_sadmin) {
                                          alert('The primary SADMIN cannot be deleted.');
                                          return;
                                        }
                                        if (u.role === 'superadmin') {
                                          alert('Super Admin users cannot be deleted.');
                                          return;
                                        }
                                        if (u.role === 'sadmin' && !isSuperadmin) {
                                          alert('SAdmin users cannot be deleted by SADMIN.');
                                          return;
                                        }
                                        if (u.role === 'cadmin' && isCadmin) {
                                          alert('Cadmin users cannot be deleted by Cadmin.');
                                          return;
                                        }
                                        handleRemoveUser(selectedStore.id, u.id);
                                      }}>Remove</Button>
                                    )}
                                  </>
                                )}
                                
                                {/* v0.4.6i: Reset Password / Reset Device button.
                                    Opens AdminResetDialog with two options
                                    (reset password, reset device). */}
                                {showResetPassword && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    title={t('admin.resetPassword')}
                                    onClick={() => setResetTarget({
                                      userId: u.id,
                                      userName: u.name || u.email,
                                      userEmail: u.email,
                                    })}
                                  >
                                    🔑
                                  </Button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>

              {selectedStore.access_control_enabled && (
                <>
                  {/* Authorized Persons */}
                  <div className="space-y-4 border-b pb-4">
                    <h3 className="font-semibold text-lg">Authorized Persons</h3>
                    <AuthorizedPersonsManager
                      storeId={selectedStore.id}
                      edgeIp={selectedStore.edge_server_ip}
                      edgePort={selectedStore.edge_server_port ?? 3002}
                    />
                  </div>

                  {/* Authorized License Plates */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-lg">Authorized License Plates</h3>
                    <AuthorizedPlatesManager storeId={selectedStore.id} />
                  </div>
                </>
                )}

              {selectedStore.guardian_enabled && (
                <>
                  {/* POI Management — simple list, restored per design call.
                      The new gallery / Add-from-Recent features live inside the
                      edit modal (POIFormModal), NOT in the list view. */}
                  <div className="space-y-4 border-b pb-4">
                    <h3 className="font-semibold text-lg">POIs</h3>
                    <PoiListManager storeId={selectedStore.id} />
                  </div>

                  {/* Bulk Upload POIs */}
                  <div className="space-y-4 border-t pt-4">
                    <h3 className="font-semibold text-lg">Bulk Upload POIs</h3>
                      <BulkPOIUpload
                        storeId={selectedStore.id}
                        storeInfo={{ country_code: selectedStore.country_code, city_code: selectedStore.city_code }}
                        existingCodes={storePoiCodes}
                        onSuccess={() => {
                          api.getStorePois(selectedStore.id).then(data => {
                            setStorePoiCodes(data.map((p: any) => p.code));
                          }).catch(err => {
                            console.error(err);
                            setStorePoiCodes([]);
                          });
                        }}
                        edgeIp={selectedStore.edge_server_ip}
                        edgePort={selectedStore.edge_server_port ?? 3002}
                      />
                  </div>
                </>
              )}
            </>  
              
          )}          
        </DialogContent>
      </Dialog>

      {/* Store Selector Modal (for admin) */}
      {showStoreSelector && (
        <StoreSelectorModal />
      )}

      {/* Alerts Log Modal */}
      {showAlertsLog && (
        <AlertsLogModal
          alerts={alertsLog}
          onClose={() => setShowAlertsLog(false)}
          onPoiClick={(alert) => {
            if (alert.poi) {
              setSelectedAlertPoi({
                ...alert.poi,
                lastSeen: alert.createdAt,
              });
              setShowAlertsLog(false);
            } else if (alert.event_type === 'access_control') {
              setSelectedAccessAlert(alert);
              setShowAlertsLog(false);
            }
          }}
        />
      )}

      {/* ────── Own POIs Filter Modal ────── */}
      {showOwnPoisModal && (
        <POIsFilterModal
          pois={markedPois.filter(p => !p.isNetwork)}
          title={t('admin.totalPOIsOwn')}
          onClose={() => setShowOwnPoisModal(false)}
        />
      )}

      {/* ────── Network POIs Filter Modal ────── */}
      {showNetworkPoisModal && (
        <POIsFilterModal
          pois={markedPois.filter(p => p.isNetwork)}
          title={t('admin.totalPOIsNetwork')}
          onClose={() => setShowNetworkPoisModal(false)}
          showNetworkBadge
        />
      )}

      {/* Authorized Persons Modal */}
      {showAuthorizedPersonsModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{t('admin.authorizedPersons')}</h2>
              <button onClick={() => setShowAuthorizedPersonsModal(false)} className="p-1"><X className="w-5 h-5" /></button>
            </div>
            {allPersons.length === 0 ? (
              <p className="text-sm text-muted-foreground">No authorized persons found.</p>
            ) : (
              <div className="divide-y">
                {allPersons.map((p: any) => (
                  <div key={p.id} className="flex items-center justify-between p-2">
                    <div className="flex items-center gap-3">
                      {p.photo_url ? (
                        <img src={p.photo_url} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-muted" />
                      )}
                      <div>
                        <p className="font-medium">{p.name}</p>
                        {p.notes && <p className="text-xs text-muted-foreground">{p.notes}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Authorized Vehicles Modal */}
      {showAuthorizedPlatesModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{t('admin.authorizedPlates')}</h2>
              <button onClick={() => setShowAuthorizedPlatesModal(false)} className="p-1"><X className="w-5 h-5" /></button>
            </div>
            {allPlates.length === 0 ? (
              <p className="text-sm text-muted-foreground">No authorized plates found.</p>
            ) : (
              <div className="divide-y">
                {allPlates.map((p: any) => (
                  <div key={p.id} className="flex items-center justify-between p-2">
                    <div className="flex items-center gap-3">
                      {p.photo_url ? (
                        <img src={p.photo_url} className="w-12 h-12 object-cover rounded" />
                      ) : (
                        <div className="w-12 h-12 rounded bg-muted" />
                      )}
                      <div>
                        <p className="font-mono font-bold">{p.license_plate}</p>
                        <p className="text-sm">{p.make} {p.model} – {p.color}</p>
                        {p.notes && <p className="text-xs text-muted-foreground">{p.notes}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* POI Detail Modal from alert log */}
      {selectedAlertPoi && (
      <POIDetailModal
        poi={selectedAlertPoi}
        onClose={() => {
          setSelectedAlertPoi(null);
          setShowAlertsLog(true);    // ← reopen the alert list
        }}
        onEdit={handleEdit}
        onDelete={async (poiId) => await handleDeletePoi(poiId)}
        readOnly={true}
      />
      )}

      {/* Access Control alert detail */}
      {selectedAccessAlert && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-lg shadow-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Unauthorised Access</h2>
              <button
                onClick={() => {
                  setSelectedAccessAlert(null);
                  setShowAlertsLog(true);    // ← reopen the alert list
                }}
                className="p-1 rounded-full hover:bg-muted"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Capture image */}
            <div className="aspect-video bg-gray-200 dark:bg-gray-800 rounded-md overflow-hidden mb-4">
              {selectedAccessAlert.capture_image_url ? (
                <img
                  src={selectedAccessAlert.capture_image_url}
                  alt="Unauthorised"
                  className="w-full h-full object-contain bg-muted"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                  No image available
                </div>
              )}
            </div>

            {/* Details */}
            <div className="space-y-2 text-sm">
              <p>
                <span className="font-medium">Time:</span>{' '}
                {new Date(selectedAccessAlert.createdAt).toLocaleString()}
              </p>
              <p>
                <span className="font-medium">Status:</span>{' '}
                <span className={
                  selectedAccessAlert.status === 'active' ? 'text-red-600' :
                  selectedAccessAlert.status === 'acknowledged' ? 'text-blue-600' : 'text-green-600'
                }>
                  {selectedAccessAlert.status}
                </span>
              </p>
              {selectedAccessAlert.acknowledgedByName && (
                <p>
                  <span className="font-medium">Acknowledged by:</span>{' '}
                  {selectedAccessAlert.acknowledgedByName}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Remove all users modal*/}  
      {showRemoveModal && (
        <UserAddToStoresModal
          stores={stores}
          mode="remove"
          onClose={() => setShowRemoveModal(false)}
          onSuccess={() => {
            setShowRemoveModal(false);
            setSuccess('User removed from selected stores');
            setTimeout(() => setSuccess(''), 2000);
          }}
        />
      )}

      {/* Show Users Modal*/}
      {showUserAddModal && (
      <UserAddToStoresModal
        stores={stores}
        role={userAddRole}
        mode="add"
        onClose={() => setShowUserAddModal(false)}
        onSuccess={() => {
          setShowUserAddModal(false);
          setSuccess(`${userAddRole} added successfully`);
          setTimeout(() => setSuccess(''), 2000);
          if (selectedStore) fetchStoreUsers(selectedStore.id);
        }}
      />
      )}

      {/* v0.4.6i: reset-access dialog (commerce user reset site). */}
      {resetTarget && (
        <AdminResetDialog
          open={!!resetTarget}
          onClose={() => setResetTarget(null)}
          userId={resetTarget.userId}
          userName={resetTarget.userName}
          userEmail={resetTarget.userEmail}
        />
      )}
    </div>
  );
}
